import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { centsToEuros } from '@/constants'
import { CampaignInvoicePaymentData } from '@jobilla/nexus-sdk'
import { useTranslation } from 'react-i18next'
import { CampaignInvoiceStatusBadge } from '../../../constructs'
import { CampaignInvoiceModel } from '../../shared'

export type InvoicePanelProps = {
	invoice?: CampaignInvoiceModel
	isLoading: boolean
	payments?: CampaignInvoicePaymentData[]
}

export function InvoicePanel({ invoice, isLoading, payments }: InvoicePanelProps) {
	const { t } = useTranslation('common')

	if (isLoading) {
		return (
			<div className="space-y-4">
				<h2 className="text-xl font-semibold text-grey-900">{t('overview.invoice.title')}</h2>
				<div className="p-4 bg-white rounded-xl border border-grey-100">
					<div className="flex items-center gap-4">
						<div className="bg-grey-100 p-3 rounded-lg animate-pulse">
							<div className="size-8" />
						</div>
						<div className="flex-1">
							<div className="h-5 w-16 bg-grey-200 rounded animate-pulse" />
						</div>
						<div className="h-8 w-20 bg-grey-100 rounded-full animate-pulse" />
					</div>
				</div>
			</div>
		)
	}

	if (!invoice) {
		return null
	}

	const hasFailedPayment = payments?.some((payment) => payment.status === 'failed')
	const failedPayment = payments?.find((payment) => payment.status === 'failed')

	return (
		<div className="space-y-4">
			<h2 className="text-xl font-semibold text-grey-900">{t('overview.invoice.title')}</h2>
			<div className="p-4 bg-white rounded-xl border border-grey-100">
				<div className="flex items-center gap-4">
					<div className="bg-purple-100 p-3 rounded-lg text-purple-600">
						<ReceiptIcon />
					</div>
					<div>
						<p>€{centsToEuros(invoice.total)}</p>
					</div>
					<div className="flex-1 min-w-0">
						<CampaignInvoiceStatusBadge invoice={invoice} />
					</div>

					{invoice.hostedInvoiceUrl && (
						<>
							<a
								href={invoice.hostedInvoiceUrl}
								target="_blank"
								className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-full hover:bg-purple-100 transition-colors"
							>
								{t('overview.invoice.viewInvoice')}
							</a>
						</>
					)}
				</div>

				{hasFailedPayment && failedPayment && (
					<div className="mt-4">
						<Alert variant="destructive">
							<AlertTitle>{t('overview.invoice.paymentFailed')}</AlertTitle>
							<AlertDescription>
								{t('overview.invoice.paymentFailedDesc', {
									amount: centsToEuros(failedPayment.amount),
								})}
							</AlertDescription>
						</Alert>
					</div>
				)}
			</div>
		</div>
	)
}

function ReceiptIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			className="size-8"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185ZM9.75 9h.008v.008H9.75V9Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008V13.5Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
			/>
		</svg>
	)
}
